// Sprint 65 — Deal Baazar Admin.
//
// Manages `settings.marketing.deal_campaigns[]` and `settings.marketing.deal_banners[]`
// through the existing PATCH /store/settings/marketing endpoint. No new APIs.

import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SuperAdminStoreGate from "@/components/SuperAdminStoreGate";
import ImageUpload from "@/components/ImageUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Plus, Trash2, Flame, Copy, ChevronDown, ChevronRight, Search, Package } from "lucide-react";
import { DEAL_TEMPLATES, slugify, activeCampaigns, msRemaining, fmtRemaining } from "@/lib/dealPricing";
import { useAuth, isSuperAdmin } from "@/contexts/AuthContext";

const rid = (p) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const newCampaign = (tpl) => ({
  id: rid("dc"),
  name: tpl ? `${tpl.emoji} ${tpl.label}` : "New Deal Campaign",
  slug: slugify(tpl?.label || "new-deal"),
  type: tpl?.key || "flash_sale",
  banner_desktop: "",
  banner_mobile: "",
  hero_banner: "",
  theme_color: tpl?.theme || "#DC1E1E",
  priority: 100,
  start_date: "",
  end_date: "",
  is_active: true,
  show_timer: tpl?.show_timer ?? true,
  flat_discount: 0,
  pct_discount: 0,
  products: [],
});

const newBanner = () => ({
  id: rid("db"),
  title: "",
  subtitle: "",
  banner_desktop: "",
  banner_mobile: "",
  link_to: "",
  priority: 100,
  is_active: true,
  start_date: "",
  end_date: "",
});

// Small product picker: search + pick from /products?q=
function ProductPicker({ selected, onChange, liveById, onLive }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: "20" });
        if (q) params.set("q", q);
        const { data } = await api.get(`/products?${params.toString()}`);
        const items = data?.items || [];
        if (!cancelled) {
          setRows(items);
          if (typeof onLive === "function") onLive(items);         // seed parent cache
        }
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const selMap = useMemo(() => {
    const m = {}; (selected || []).forEach((s) => { m[s.product_id] = s; }); return m;
  }, [selected]);

  const toggle = (p) => {
    // Sprint 65.1 — Persist ONLY {product_id, deal_price, tag}. Everything else
    // (name, image, price, stock, brand, rating, …) is hydrated live at render.
    if (selMap[p.id]) onChange((selected || []).filter((s) => s.product_id !== p.id));
    else onChange([...(selected || []), { product_id: p.id, deal_price: "", tag: "" }]);
  };

  const patchLine = (pid, patch) => onChange((selected || []).map((s) => s.product_id === pid ? { ...s, ...patch } : s));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products by name…"
            className="pl-8"
            data-testid="deal-picker-search"
          />
        </div>
        <span className="text-xs text-muted-foreground shrink-0">{(selected || []).length} selected</span>
      </div>

      <div className="grid md:grid-cols-2 gap-2 max-h-56 overflow-auto border rounded-lg p-2 bg-muted/20">
        {loading ? <Skeleton className="h-6 w-full" /> :
         rows.length === 0 ? <div className="col-span-2 text-xs text-muted-foreground text-center py-4">No products match.</div> :
         rows.map((p) => {
          const active = !!selMap[p.id];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p)}
              className={`flex items-center gap-2 text-left rounded-lg px-2 py-1.5 border text-xs transition ${active ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40"}`}
              data-testid={`deal-pick-${p.id}`}
            >
              <div className="w-8 h-8 rounded bg-muted grid place-items-center shrink-0 overflow-hidden">
                {p.thumbnail_url ? <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-4 h-4 opacity-40" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{p.name}</div>
                <div className="text-[10px] text-muted-foreground tabular-nums">₹{Math.round(p.selling_price)} · MRP ₹{Math.round(p.mrp || p.selling_price)}</div>
              </div>
              <span className={`text-[10px] font-bold ${active ? "text-primary" : "text-muted-foreground"}`}>{active ? "REMOVE" : "ADD"}</span>
            </button>
          );
        })}
      </div>

      {(selected || []).length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_100px_120px_36px] gap-2 px-2 py-1.5 bg-muted/40 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            <div>Product</div><div>Orig ₹</div><div>Deal ₹</div><div>Tag</div><div></div>
          </div>
          {(selected || []).map((s) => {
            const live = (liveById && liveById[s.product_id]) || {};
            const isDeleted = live && live.is_active === false;
            return (
            <div key={s.product_id} className={`grid grid-cols-[1fr_100px_100px_120px_36px] gap-2 px-2 py-1.5 items-center border-t text-xs ${isDeleted ? "bg-red-50/60" : ""}`} data-testid={`deal-line-${s.product_id}`}>
              <div className="truncate">
                <div className="truncate">{live.name || <span className="text-muted-foreground italic">Loading…</span>}</div>
                {isDeleted && <div className="text-[9px] font-bold text-destructive uppercase tracking-widest">Inactive — auto-hidden</div>}
                {!isDeleted && live.stock === 0 && <div className="text-[9px] font-bold text-amber-700 uppercase tracking-widest">Out of stock — shows Notify-Me</div>}
              </div>
              <div className="tabular-nums text-muted-foreground">
                {live.selling_price != null ? `₹${Math.round(live.selling_price)}` : "—"}
              </div>
              <Input
                type="number" min="0"
                value={s.deal_price ?? ""}
                onChange={(e) => patchLine(s.product_id, { deal_price: e.target.value ? Number(e.target.value) : "" })}
                placeholder="e.g. 120"
                className="h-8 text-xs"
              />
              <Input
                value={s.tag || ""}
                onChange={(e) => patchLine(s.product_id, { tag: e.target.value })}
                placeholder="near-expiry"
                className="h-8 text-xs"
              />
              <Button variant="ghost" size="sm" onClick={() => toggle({ id: s.product_id })} className="p-0 h-8 w-8">
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CampaignEditor({ item, onChange, onRemove, onDuplicate, liveById, onLive, advanced = true }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...item, ...patch });
  const ms = msRemaining(item);
  return (
    <div className="border rounded-2xl bg-card overflow-hidden" data-testid={`deal-campaign-${item.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30">
        <button onClick={() => setOpen((v) => !v)} className="p-1 rounded hover:bg-muted transition" aria-label="Toggle">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div
          className="w-6 h-6 rounded shrink-0 border"
          style={{ background: item.theme_color || "#DC1E1E" }}
        />
        <div className="min-w-0 flex-1">
          <div className="font-bold truncate text-sm">{item.name || "Untitled"}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            /deal-baazar/{item.slug || slugify(item.name)} · {(item.products || []).length} products
            {ms != null && ms > 0 && <> · Ends in {fmtRemaining(ms)}</>}
          </div>
        </div>
        <label className="flex items-center gap-1 text-xs">
          <Switch checked={!!item.is_active} onCheckedChange={(v) => set({ is_active: v })} className="data-[state=checked]:bg-primary" />
          Active
        </label>
        {advanced && <Button variant="ghost" size="sm" onClick={onDuplicate} title="Duplicate"><Copy className="w-4 h-4" /></Button>}
        <Button variant="ghost" size="sm" onClick={onRemove} title="Delete"><Trash2 className="w-4 h-4 text-destructive" /></Button>
      </div>

      {open && (
        <div className="p-4 space-y-4">
          {/* Phase 4 — STORE_ADMIN gets a stripped-down editor.
              Advanced controls (theme, priority, dates, discounts, banners,
              timer, analytics strip) are visible to SUPER_ADMIN only. Store
              admins can still name the campaign and pick the products that
              go into it — the two things they need to launch a drop. */}
          {advanced ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <div><Label>Campaign Name</Label><Input value={item.name} onChange={(e) => set({ name: e.target.value, slug: item.slug || slugify(e.target.value) })} placeholder="🔥 Flash Sale" /></div>
                <div><Label>URL Slug</Label><Input value={item.slug} onChange={(e) => set({ slug: slugify(e.target.value) })} placeholder="flash-sale" /></div>
                <div><Label>Theme Colour</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={item.theme_color || "#DC1E1E"} onChange={(e) => set({ theme_color: e.target.value })} className="h-9 w-14 rounded border" />
                    <Input value={item.theme_color || "#DC1E1E"} onChange={(e) => set({ theme_color: e.target.value })} className="flex-1" />
                  </div>
                </div>
                <div><Label>Priority</Label><Input type="number" value={item.priority} onChange={(e) => set({ priority: Number(e.target.value) || 0 })} /></div>
                <div><Label>Start Date</Label><Input type="datetime-local" value={item.start_date || ""} onChange={(e) => set({ start_date: e.target.value })} /></div>
                <div><Label>End Date</Label><Input type="datetime-local" value={item.end_date || ""} onChange={(e) => set({ end_date: e.target.value })} /></div>
                <div><Label>Flat Discount ₹ (all products)</Label><Input type="number" value={item.flat_discount || 0} onChange={(e) => set({ flat_discount: Number(e.target.value) || 0 })} /></div>
                <div><Label>Percentage Discount % (all products)</Label><Input type="number" value={item.pct_discount || 0} onChange={(e) => set({ pct_discount: Number(e.target.value) || 0 })} /></div>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <Switch checked={item.show_timer !== false} onCheckedChange={(v) => set({ show_timer: v })} className="data-[state=checked]:bg-primary" />
                  Show countdown timer
                </label>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>Hero Banner (used on Deal Baazar rotator)</Label>
                  <ImageUpload value={item.hero_banner} onChange={(v) => set({ hero_banner: v })} label="Hero" aspect="banner" testId={`deal-hero-${item.id}`} />
                </div>
                <div>
                  <Label>Desktop Banner</Label>
                  <ImageUpload value={item.banner_desktop} onChange={(v) => set({ banner_desktop: v })} label="Desktop" aspect="banner" testId={`deal-bd-${item.id}`} />
                </div>
                <div>
                  <Label>Mobile Banner</Label>
                  <ImageUpload value={item.banner_mobile} onChange={(v) => set({ banner_mobile: v })} label="Mobile" aspect="square" testId={`deal-bm-${item.id}`} />
                </div>
              </div>
            </>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Campaign Name</Label><Input value={item.name} onChange={(e) => set({ name: e.target.value, slug: item.slug || slugify(e.target.value) })} placeholder="🔥 Flash Sale" data-testid="deal-campaign-name" /></div>
              <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
                Advanced settings (theme, dates, banners, discounts) are managed by the platform admin.
                You can pick products below.
              </div>
            </div>
          )}

          <div>
            <Label className="mb-1 block">Products in this campaign</Label>
            <ProductPicker
              selected={item.products}
              onChange={(next) => set({ products: next })}
              liveById={liveById}
              onLive={onLive}
            />
          </div>

          {/* Analytics quick strip — Super Admin only */}
          {advanced && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <StatCard label="Products" value={(item.products || []).length} />
            <StatCard label="Views" value={item.views || 0} />
            <StatCard label="Clicks" value={item.clicks || 0} />
            <StatCard label="Status" value={item.is_active ? "LIVE" : "PAUSED"} highlight={item.is_active ? "green" : "red"} />
            <StatCard label="Remaining" value={ms == null ? "No end" : fmtRemaining(ms)} />
          </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  const bg = highlight === "green" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
           : highlight === "red"   ? "bg-red-50 border-red-200 text-red-800"
           : "bg-muted/30 border-border/60 text-foreground";
  return (
    <div className={`rounded-lg border px-2 py-1.5 ${bg}`}>
      <div className="text-[9px] uppercase font-bold tracking-widest opacity-70">{label}</div>
      <div className="tabular-nums font-black text-sm truncate">{value}</div>
    </div>
  );
}

function BannerEditor({ item, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...item, ...patch });
  return (
    <div className="border rounded-2xl bg-card overflow-hidden" data-testid={`deal-banner-${item.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30">
        <button onClick={() => setOpen((v) => !v)} className="p-1 rounded hover:bg-muted transition">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-bold truncate text-sm">{item.title || "Untitled banner"}</div>
          <div className="text-[10px] text-muted-foreground truncate">{item.link_to || "no link"}</div>
        </div>
        <label className="flex items-center gap-1 text-xs">
          <Switch checked={!!item.is_active} onCheckedChange={(v) => set({ is_active: v })} className="data-[state=checked]:bg-primary" />
          Active
        </label>
        <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="w-4 h-4 text-destructive" /></Button>
      </div>
      {open && (
        <div className="p-4 grid md:grid-cols-2 gap-3">
          <ImageUpload value={item.banner_desktop} onChange={(v) => set({ banner_desktop: v })} label="Desktop banner" aspect="banner" testId={`deal-bd-${item.id}`} />
          <ImageUpload value={item.banner_mobile}  onChange={(v) => set({ banner_mobile: v })}  label="Mobile banner"  aspect="square" testId={`deal-bm-${item.id}`} />
          <div><Label>Title</Label><Input value={item.title} onChange={(e) => set({ title: e.target.value })} placeholder="Weekend Blast" /></div>
          <div><Label>Subtitle</Label><Input value={item.subtitle} onChange={(e) => set({ subtitle: e.target.value })} placeholder="Up to 60% OFF" /></div>
          <div><Label>Link to</Label><Input value={item.link_to} onChange={(e) => set({ link_to: e.target.value })} placeholder="/deal-baazar/flash-sale" /></div>
          <div><Label>Priority</Label><Input type="number" value={item.priority} onChange={(e) => set({ priority: Number(e.target.value) || 0 })} /></div>
          <div><Label>Start Date</Label><Input type="datetime-local" value={item.start_date || ""} onChange={(e) => set({ start_date: e.target.value })} /></div>
          <div><Label>End Date</Label><Input type="datetime-local" value={item.end_date || ""} onChange={(e) => set({ end_date: e.target.value })} /></div>
        </div>
      )}
    </div>
  );
}

export default function DealBazaarAdmin() {
  return (
    <SuperAdminStoreGate
      pageTitle="Deal Baazar"
      pageSubtitle="Manage exclusive deal campaigns and banners for a specific store."
    >
      <DealBazaarAdminInner />
    </SuperAdminStoreGate>
  );
}

function DealBazaarAdminInner() {
  const { user } = useAuth();
  // Phase 4 — Store admins see a stripped-down Deal Baazar: they can
  // pick a template + choose products, but every other control (banners
  // tab, theme, dates, discounts, analytics, duplicate) is Super Admin-only.
  const advanced = isSuperAdmin(user);
  const [marketing, setMarketing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("campaigns");
  const [liveById, setLiveById] = useState({});   // Sprint 65.1 — live product cache

  const seedLive = (list) => {
    if (!Array.isArray(list) || !list.length) return;
    setLiveById((prev) => {
      const next = { ...prev };
      list.forEach((p) => { if (p && p.id) next[p.id] = p; });
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/settings");
      const mkt = data?.settings?.marketing || {};
      setMarketing({
        ...mkt,
        deal_campaigns: Array.isArray(mkt.deal_campaigns) ? mkt.deal_campaigns : [],
        deal_banners:   Array.isArray(mkt.deal_banners)   ? mkt.deal_banners   : [],
      });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Sprint 65.1 — hydrate live product data for every product_id already
  // referenced by any campaign so the editor shows current name/price/stock.
  useEffect(() => {
    if (!marketing?.deal_campaigns?.length) return;
    const ids = new Set();
    marketing.deal_campaigns.forEach((c) => (c.products || []).forEach((p) => p.product_id && ids.add(p.product_id)));
    const missing = [...ids].filter((id) => !liveById[id]);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const fetched = await Promise.all(
        missing.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
      );
      if (!cancelled) seedLive(fetched.filter(Boolean));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketing]);

  const saveAll = async () => {
    setSaving(true);
    try {
      // The marketing endpoint REPLACES the whole subtree, so we send back
      // the merged marketing object to preserve announcement_bar / sliders / etc.
      await api.patch("/store/settings/marketing", marketing || {});
      toast.success("Deal Baazar saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const campaigns = marketing?.deal_campaigns || [];
  const banners   = marketing?.deal_banners || [];
  const setCampaigns = (next) => setMarketing((m) => ({ ...(m || {}), deal_campaigns: next }));
  const setBanners   = (next) => setMarketing((m) => ({ ...(m || {}), deal_banners: next }));

  const addFromTemplate = (tpl) => setCampaigns([...(campaigns || []), newCampaign(tpl)]);
  const patchCampaign = (id, next) => setCampaigns(campaigns.map((c) => c.id === id ? next : c));
  const removeCampaign = (id) => setCampaigns(campaigns.filter((c) => c.id !== id));
  const duplicateCampaign = (c) => setCampaigns([...campaigns, { ...c, id: rid("dc"), name: `${c.name} (copy)`, slug: `${slugify(c.name)}-copy` }]);

  const live = activeCampaigns(campaigns).length;

  return (
    <div className="p-6" data-testid="deal-bazaar-admin">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><Flame className="w-6 h-6 text-primary" />Deal Baazar</span>}
        subtitle="Manage exclusive deal campaigns and banners. Deal prices apply only inside Deal Baazar."
        actions={<Button onClick={saveAll} disabled={saving || loading} className="btn-primary-glow" data-testid="deal-save-btn"><Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save all"}</Button>}
      />

      {loading || !marketing ? (
        <div className="space-y-3"><Skeleton className="h-10 w-72" /><Skeleton className="h-96 w-full" /></div>
      ) : (
        <>
          <div className="flex items-center flex-wrap gap-2 mb-4">
            <StatCard label="Total campaigns" value={campaigns.length} />
            <StatCard label="Live now"        value={live} highlight="green" />
            {advanced && <StatCard label="Deal banners" value={banners.length} />}
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
              {advanced && (
                <TabsTrigger value="banners"   data-testid="tab-banners">Deal Banners</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="campaigns" className="mt-4 space-y-3">
              <Card className="card-soft border-transparent">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-display">Quick-start templates</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {DEAL_TEMPLATES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => addFromTemplate(t)}
                        className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-primary hover:text-primary-foreground hover:border-primary transition"
                        style={{ borderColor: t.theme }}
                        data-testid={`deal-tpl-${t.key}`}
                      >
                        <span>{t.emoji}</span>{t.label}<Plus className="w-3 h-3" />
                      </button>
                    ))}
                    <button
                      onClick={() => addFromTemplate(null)}
                      className="inline-flex items-center gap-1 rounded-full border-2 border-dashed px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                      data-testid="deal-tpl-blank"
                    >
                      Blank campaign <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </CardContent>
              </Card>

              {campaigns.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed p-10 text-center text-muted-foreground">
                  No campaigns yet. Click a quick-start template above to launch your first drop.
                </div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map((c) => (
                    <CampaignEditor
                      key={c.id}
                      item={c}
                      onChange={(next) => patchCampaign(c.id, next)}
                      onRemove={() => removeCampaign(c.id)}
                      onDuplicate={() => duplicateCampaign(c)}
                      liveById={liveById}
                      onLive={seedLive}
                      advanced={advanced}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {advanced && (
            <TabsContent value="banners" className="mt-4 space-y-3">
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setBanners([...banners, newBanner()])} data-testid="add-deal-banner">
                  <Plus className="w-4 h-4 mr-1" /> Add banner
                </Button>
              </div>
              {banners.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed p-10 text-center text-muted-foreground">
                  No Deal Baazar banners yet. Upload one to slot between campaign rails.
                </div>
              ) : banners.map((b) => (
                <BannerEditor
                  key={b.id}
                  item={b}
                  onChange={(next) => setBanners(banners.map((x) => x.id === b.id ? next : x))}
                  onRemove={() => setBanners(banners.filter((x) => x.id !== b.id))}
                />
              ))}
            </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}
